import { CommonModule, CurrencyPipe, registerLocaleData } from '@angular/common';
import { inject, Injector, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from '../../mif/app/app-routing.module';
import { constants } from './shared/constants/constants';

import { HTTP_INTERCEPTORS, HttpInterceptor, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, Routes } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { MifSubscriptionAuthService } from '@shared/auth/mif-subscription.auth.service';
import { SubscriptionGuard } from '@shared/auth/subscription.guard';
import { OtpFormControlComponent } from '@shared/form/otp-form-control/otp-form-control.component';
import { FilesUploadingInterceptor } from '@shared/interceptors/files-uploading.interceptor';
import { MonitoringService } from '@webapi/services/monitoring.service';
import { PortfolioService } from '@webapi/services/portfolio.service';
import { BaseChartDirective, provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { BsModalService, ModalModule } from 'ngx-bootstrap/modal';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { TooltipModule } from 'primeng/tooltip';
import { AdminComponent } from 'projects/mif-admin/app/admin.component';
import { AdminAuthGuard } from 'projects/mif-admin/app/auth/admin-auth.guard';
import { AdminAuthInterceptor } from 'projects/mif-admin/app/auth/admin-auth.interceptor';
import { AdminAuthService } from 'projects/mif-admin/app/auth/admin-auth.service';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { FinishComponent } from './features/finish/finish.component';
import { FooterComponent } from './features/footer/footer.component';
import { HeaderComponent } from './features/header/header.component';
import { AdvertisementSourceClientComponent } from './features/membership-with-otp-check/components/advertisement-source-client/advertisement-source-client.component';
import { AuthenticationClientComponent } from './features/membership-with-otp-check/components/authentication-client/authentication-client.component';
import { AuthenticationProspectComponent } from './features/membership-with-otp-check/components/authentication-prospect/authentication-prospect.component';
import { ClientProspectSwitcherComponent } from './features/membership-with-otp-check/components/client-prospect-switcher/client-prospect-switcher.component';
import { OtpCheckComponent } from './features/membership-with-otp-check/components/otp-check/otp-check.component';
import { RegistrationFormComponent } from './features/membership-with-otp-check/components/registration-form/registration-form.component';
import { MembershipWithOtpCheckComponent } from './features/membership-with-otp-check/membership-with-otp-check.component';
import { ClauseLibreComponent } from './features/my-beneficiaries/clause-libre/clause-libre.component';
import { ClauseNominativeComponent } from './features/my-beneficiaries/clause-nominative/clause-nominative.component';
import { MyBeneficiariesComponent } from './features/my-beneficiaries/my-beneficiaries.component';
import { DistributionComponent } from './features/my-contract/distribution/distribution.component';
import { MyContractComponent } from './features/my-contract/my-contract.component';
import { PieDistributionComponent } from './features/my-contract/pie-distribution/pie-distribution.component';
import { BankDetailsComponent } from './features/my-details/bank-details/bank-details.component';
import { ContactsComponent } from './features/my-details/contacts/contacts.component';
import { FinancialSituationComponent } from './features/my-details/financial-situation/financial-situation.component';
import { MyDetailsComponent } from './features/my-details/my-details.component';
import { AdermConfirmationComponent } from './features/my-profile/modals/aderm-confirmation/aderm-confirmation.component';
import { MyProfileComponent } from './features/my-profile/my-profile.component';
import { ProfileStepsComponent } from './features/my-profile/profile-steps/profile-steps.component';
import { ProfileSummaryComponent } from './features/my-profile/profile-summary/profile-summary.component';
import { PhoneConfirmationComponent } from './features/phone-confirmation/phone-confirmation.component';
import { ResidenceQuestionsComponent } from './features/residence-questions/residence-questions.component';
import { SubscriptionCompleteComponent } from './features/subscription-complete/subscription-complete.component';
import { HealthQuestionnaireComponent } from './gpa/components/health-questionnaire/health-questionnaire.component';
import { MyRateComponent } from './gpa/components/my-rate/my-rate.component';
import { MainPageComponent } from './main-page/main-page.component';
import { AnswersDataService } from './services/answers-data.service';
import { AppConfigService } from './services/app.config.service';
import { LabelsDataService } from './services/labels-data.service';
import { LazyLoadingScriptService } from './services/lazy-loading-script.service';
import { ProgressBarService } from './services/progress-bar.service';
import { SpinnerService } from './services/spinner.service';
import { MifClientTokenStorageService } from './shared/auth/mif-client-token-storage.service';
import { MifSubscriptionTokenInterceptor } from './shared/auth/mif-subscription.token.interceptor';
import { PublicTokenStorageService } from './shared/auth/public-token-storage.service';
import { MifLoginComponent } from './shared/components/auth/mif-login/mif-login.component';
import { ResumeCheckComponent } from './shared/components/auth/resume-check/resume-check.component';
import { SubscriptionTimeEndComponent } from './shared/components/auth/subscription-time-end/subscription-time-end.component';
import { SharedComponentsModule } from './shared/components/shared-components.module';
import { DebounceDirective } from './shared/directives/debounce-click.directive';
import { setAppInjector } from './shared/extensions/app-injector';
import { FormModule } from './shared/form/form.module';
import { SpinnerInterceptor } from './shared/interceptors/spinner.interceptor';
import { PipesModule } from './shared/pipes/pipes.module';
import { Client, PARROT_API_BASE_URL } from './shared/webapi/MIF.Subscription.Parrot';
import {
  API_BASE_URL,
  AuthClient,
  CustomerProfileClient,
  LabelsClient,
  LookupsClient,
  MonitoringClient,
  OffersClient,
  PortfolioClient,
  ReportingClient,
  SettingsClient,
  ParrotClient
} from './shared/webapi/MIF.Subscription.WebApi';

import { CustomerService } from './shared/webapi/services/customer.service';
import { LookupsService } from './shared/webapi/services/lookups.service';
import { OffersService } from './shared/webapi/services/offers.service';
import { ReportingService } from './shared/webapi/services/reporting.service';
import { SettingsService } from './shared/webapi/services/settings.service';
import { ParrotService } from '@webapi/services/parrot.service';
import { WsCoordonneesBancairesService } from './shared/webapi/services/ws-coordonnees-bancaires.service';
import { WsCrmQuestionnaireService } from './shared/webapi/services/ws-crm-questionnaire.service';
import { WsCRMClientService } from './shared/webapi/services/ws-crmclient.service';
import { WsDqeService } from './shared/webapi/services/ws-dqe.service';
import { WsGetPolices } from './shared/webapi/services/ws-get-polices.service';
import { WsPolicesService } from './shared/webapi/services/ws-polices.service';
import { WsReferentielMetierService } from './shared/webapi/services/ws-referentiel-metier.service';
import { WsSepaliaService } from './shared/webapi/services/ws-sepalia.service';

import { SignatureGfdPlanComponent } from './signature/gfd/signature-gfd-plan/signature-gfd-plan.component';
import { SignatureGpaPlanComponent } from './signature/gpa/signature-gpa-plan/signature-gpa-plan.component';
import { SignatureComponent } from './signature/signature.component';
import { ClientEffects } from './store/client-store/client.effects';
import { clientFeatureKey, clientReducer } from './store/client-store/client.reducer';

const routes: Routes = [
  {
    path: 'mif-login',
    component: MifLoginComponent,
    data: { name: 'mif-login', roles: ['all'] }
  },
  {
    path: '',
    data: { name: '', roles: ['cems'] },
    loadChildren: () => import('../bundles/cems/cems.module').then(m => m.CemsModule)
  },
  {
    path: '',
    data: { name: '', roles: ['gpa'] },
    loadChildren: () => import('../bundles/gpa/gpa.module').then(m => m.GpaModule)
  },
  {
    path: '',
    data: { name: '', roles: ['gfd'] },
    loadChildren: () => import('../bundles/gfd/gfd.module').then(m => m.GfdModule)
  },
  {
    path: '',
    data: { name: '', roles: ['peri'] },
    loadChildren: () => import('../bundles/peri/peri.module').then(m => m.PeriModule)
  },
  {
    path: '',
    data: { name: '', roles: ['cims'] },
    loadChildren: () => import('../bundles/cims/cims.module').then(m => m.CimsModule)
  },
  {
    path: '',
    data: { name: '', roles: ['admin'] },
    loadChildren: () => import('../../mif-admin/app/admin.module').then(m => m.AdminModule)
  }
];

registerLocaleData(localeFr);

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 0,
    exitDuration: 0
  }
};

const mifStoreImports =
  environment.orgId !== constants.orgId.admin.value
    ? [
        StoreModule.forRoot({
          [clientFeatureKey]: clientReducer
        }),
        EffectsModule.forRoot([ClientEffects])
      ]
    : [];

function GoogleTagManagerId(appConfigService: AppConfigService) {
  return appConfigService.googleTagManagerId;
}

@NgModule({
  declarations: [
    AppComponent,
    AdminComponent,
    MainPageComponent,
    MifLoginComponent,
    HeaderComponent,
    FooterComponent,
    OtpFormControlComponent,
    OtpCheckComponent,
    AuthenticationClientComponent,
    AuthenticationProspectComponent,
    ClientProspectSwitcherComponent,
    RegistrationFormComponent,
    AdvertisementSourceClientComponent,
    MembershipWithOtpCheckComponent,
    ResidenceQuestionsComponent,
    MyProfileComponent,
    AdermConfirmationComponent,
    ProfileStepsComponent,
    MyContractComponent,
    MyBeneficiariesComponent,
    MyDetailsComponent,
    SignatureComponent,
    ProfileSummaryComponent,
    ClauseNominativeComponent,
    ClauseLibreComponent,
    ContactsComponent,
    FinancialSituationComponent,
    BankDetailsComponent,
    PhoneConfirmationComponent,
    FinishComponent,
    DistributionComponent,
    PieDistributionComponent,
    HealthQuestionnaireComponent,
    MyRateComponent,
    SignatureGpaPlanComponent,
    SignatureGfdPlanComponent,
    ResumeCheckComponent,
    SubscriptionTimeEndComponent,
    SubscriptionCompleteComponent,
    DebounceDirective
  ],
  imports: [
    NgxMaskDirective,
    NgxMaskPipe,
    PipesModule,
    BrowserModule,
    AppRoutingModule,
    NgbModule,
    FontAwesomeModule,
    CommonModule,
    TooltipModule,
    FormModule,
    FormsModule,
    ReactiveFormsModule,
    NoopAnimationsModule,
    MatCheckboxModule,
    BaseChartDirective,
    MatInputModule,
    ModalModule.forRoot(),
    SharedComponentsModule,
    MatToolbarModule,
    MatIconModule,
    MatSidenavModule,
    MatListModule,
    MatMenuModule,
    MatExpansionModule,
    MatAutocompleteModule,
    ...mifStoreImports
  ],
  exports: [],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() =>
      initApp(
        inject(Injector),
        inject(WsCRMClientService),
        inject(WsReferentielMetierService),
        inject(LabelsDataService)
      )()
    ),
    provideAppInitializer(() => inject(AppConfigService).init()),
    provideNgxMask(),
    provideCharts(withDefaultRegisterables()),
    AppConfigService,
    {
      provide: 'googleTagManagerId',
      useFactory: GoogleTagManagerId,
      deps: [AppConfigService]
    },
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    {
      provide: PARROT_API_BASE_URL,
      useValue: environment.parrotApiBaseUrl
    },
    {
      provide: API_BASE_URL,
      useValue: environment.apiBaseUrl
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FilesUploadingInterceptor,
      multi: true
    },
    LazyLoadingScriptService,
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: initAuthInterceptor,
      deps: [AdminAuthService, Injector, API_BASE_URL, PARROT_API_BASE_URL],
      multi: true
    },
    CurrencyPipe,
    AnswersDataService,
    ProgressBarService,
    LabelsDataService,
    PublicTokenStorageService,
    MifClientTokenStorageService,
    SpinnerService,
    Client,
    LabelsClient,
    CustomerProfileClient,
    WsCRMClientService,
    WsReferentielMetierService,
    WsSepaliaService,
    WsCrmQuestionnaireService,
    CustomerService,
    WsPolicesService,
    WsGetPolices,
    WsCoordonneesBancairesService,
    WsDqeService,
    SettingsClient,
    SettingsService,
    ParrotClient,
    ParrotService,
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    AdminAuthService, // not related to AuthClient but used in AuthInterceptor for admin app
    AdminAuthGuard,
    SubscriptionGuard,
    OffersService,
    OffersClient,
    LookupsService,
    LookupsClient,
    AuthClient,
    MifSubscriptionAuthService, // related to AuthClient
    ReportingService,
    ReportingClient,
    PortfolioClient,
    PortfolioService,
    MonitoringService,
    MonitoringClient
  ],
  bootstrap: [environment.orgId !== constants.orgId.admin.value ? AppComponent : AdminComponent]
})
export class AppModule {
  constructor(
    private modalService: BsModalService,
    injector: Injector
  ) {
    setAppInjector(injector);
    this.modalService.config.keyboard = true;
    this.modalService.config.backdrop = 'static';
    this.modalService.config.ignoreBackdropClick = true;
  }
}

export function initApp(
  injector: Injector,
  wsCRMClientService: WsCRMClientService,
  wsReferentielMetierService: WsReferentielMetierService,
  labelsDataService: LabelsDataService
): () => Promise<void> {
  return () => {
    return new Promise<void>(async resolve => {
      const orgId = environment.orgId;
      const filteredRoutes = routes.filter(r => {
        return r.data?.roles.indexOf('all') !== -1 || r.data?.roles.indexOf(orgId) !== -1;
      });
      const router: Router = injector.get(Router);
      router.resetConfig(filteredRoutes);

      if (environment.orgId !== constants.orgId.admin.value) {
        await labelsDataService.initliaze();
        await wsCRMClientService.initialize();
        await wsReferentielMetierService.initialize();
      }

      resolve();
    });
  };
}

export function initAuthInterceptor(
  adminAuthService: AdminAuthService,
  injector: Injector,
  apiBaseUrl: string,
  parrotApiBaseUrl: string
): HttpInterceptor {
  switch (environment.orgId) {
    case constants.orgId.admin.value:
      return new AdminAuthInterceptor(adminAuthService);
    default:
      return new MifSubscriptionTokenInterceptor(injector, apiBaseUrl, parrotApiBaseUrl);
  }
}
